/**
 * Pakete — buchbare Bündel + Preis-Anker (Preis/Gast) für die KI. Unter dem Katalog erreichbar
 * (kein eigener Nav-Eintrag, KISS: Sidebar ≤ 9). Liste + Anlegen/Bearbeiten inkl. Positionen
 * aus dem Katalog. API: /api/packages (CRUD) + PUT /api/packages/:id/items.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { CatalogItem } from "../ui/types";
import { money, parseGermanEurosToCents } from "../ui/format";
import { Drawer, EmptyState, ErrorNote, Icon, LoadingRows, PageHeader } from "../ui/components";
import { AllergenBadges } from "../ui/lmiv";

interface Pkg {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  pricePerPersonCents: number;
  vatClass: string;
  active: boolean;
  itemsVersion?: number;
  itemCount?: number;
  // LMIV-Aggregat über alle Gänge (API): Union der Allergen-Codes + ob alle Gänge gepflegt sind.
  allergenUnion?: string[];
  allergensResolved?: boolean;
  dietaryAll?: Record<string, boolean>;
  // P2 (Speisekarten): Paket referenziert archivierte Gänge (z. B. nach „Karte ersetzen").
  hasArchivedItems?: boolean;
}
interface PkgItem {
  id: string;
  catalogItemId: string;
  qtyPerGuestMilli: number;
  courseLabel: string | null;
  name: string | null;
  priceCents: number | null;
  // LMIV je Gang (Tri-State: null = ungepflegt, [] = geprüft keine, Codes = geprüft).
  allergens?: string[] | null;
  dietary?: Record<string, boolean>;
  // P2: Gang wurde archiviert (z. B. Karte ersetzt) — Warn-Kennzeichnung im Drawer.
  itemArchivedAt?: string | null;
}

export default function PaketePage() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi<Pkg[]>("/api/packages");
  const catalogQ = useApi<CatalogItem[]>("/api/catalog/items");
  const [editing, setEditing] = useState<Pkg | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [items, setItems] = useState<PkgItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const packages = data ?? [];

  // Positionen des gerade bearbeiteten Pakets laden.
  // M-47: ein Ladefehler wurde verschluckt - der Drawer zeigte ein scheinbar leeres Paket, und
  // das naechste Hinzufuegen schickte per Complete-List-PUT NUR die eine neue Position und
  // loeschte die tatsaechlich vorhandenen. Jetzt: Fehler anzeigen, Editieren sperren, Retry.
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [itemsLoadError, setItemsLoadError] = useState<string | null>(null);
  // M-49: Versionszaehler der Positionsliste (CAS gegen Mehrtab/Out-of-order) als Ref,
  // damit die serialisierte Save-Kette nie eine veraltete Version aus einem Closure sendet.
  const itemsVersionRef = useRef(0);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  function loadItems(pkgId: string) {
    setItemsLoaded(false);
    setItemsLoadError(null);
    void apiMutate<{ package: Pkg; items: PkgItem[] }>(`/api/packages/${pkgId}`, "GET")
      .then((d) => {
        setItems(d.items);
        itemsVersionRef.current = d.package.itemsVersion ?? 0;
        setItemsLoaded(true);
      })
      .catch((e) => setItemsLoadError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(() => {
    if (!editing) return;
    setItems([]);
    loadItems(editing.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  async function saveNew(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    // N-F2: ungültigen Preis als Fehler melden statt still auf 0 zu clampen.
    const priceCents = parseGermanEurosToCents((form.get("price") as string) ?? "");
    if (priceCents == null) {
      setFormError("Ungültiger Preis pro Gast – bitte als Betrag eingeben, z. B. 45,00.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/packages", "POST", {
        name: form.get("name"),
        description: (form.get("description") as string) || undefined,
        pricePerPersonCents: priceCents,
      });
      setCreateOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function patchPkg(p: Pkg, patch: Partial<Pkg>) {
    // M-O: Fehler nicht mehr verschlucken — sonst wirkt ein fehlgeschlagenes PATCH wie „gespeichert".
    try {
      await apiMutate(`/api/packages/${p.id}`, "PATCH", patch);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function removePkg(p: Pkg) {
    if (!confirm(`Paket „${p.name}" löschen?`)) return;
    await apiMutate(`/api/packages/${p.id}`, "DELETE").catch((e) => setFormError(e instanceof Error ? e.message : String(e)));
    reload();
  }

  async function saveItems(next: PkgItem[]) {
    if (!editing || !itemsLoaded) return; // M-47: nie auf Basis eines nicht geladenen Stands schreiben
    const prev = items;
    const pkgId = editing.id;
    setItems(next); // optimistisch
    // M-49: Saves serialisieren - schnelle Folge-Klicks (Menge + Entfernen) duerfen sich nicht
    // gegenseitig ueberholen; die Versionspruefung im Worker faengt den Mehrtab-Fall ab.
    saveChainRef.current = saveChainRef.current.then(async () => {
      try {
        const res = await apiMutate<{ count: number; itemsVersion: number }>(`/api/packages/${pkgId}/items`, "PUT", {
          items: next.map((it) => ({ catalogItemId: it.catalogItemId, qtyPerGuestMilli: it.qtyPerGuestMilli, courseLabel: it.courseLabel })),
          itemsVersion: itemsVersionRef.current,
        });
        itemsVersionRef.current = res.itemsVersion;
        reload();
      } catch (e) {
        const conflict = (e as { statusCode?: number }).statusCode === 409;
        setFormError(
          conflict
            ? "Die Positionen wurden zwischenzeitlich geändert (anderer Tab?) – Stand neu geladen, bitte Änderung wiederholen."
            : e instanceof Error
              ? e.message
              : String(e),
        );
        // M-O: nach Fehler den Server-Stand (inkl. Version) neu laden statt den optimistischen
        // Stand zu behalten (Rollback auf den vorherigen Stand als Fallback, falls auch der GET scheitert).
        void apiMutate<{ package: Pkg; items: PkgItem[] }>(`/api/packages/${pkgId}`, "GET")
          .then((d) => {
            setItems(d.items);
            itemsVersionRef.current = d.package.itemsVersion ?? 0;
          })
          .catch(() => setItems(prev));
      }
    });
    await saveChainRef.current;
  }

  function addCatalogItem(ci: CatalogItem) {
    if (!editing || !itemsLoaded) return; // M-47: siehe saveItems
    if (items.some((i) => i.catalogItemId === ci.id)) return;
    void saveItems([...items, { id: crypto.randomUUID(), catalogItemId: ci.id, qtyPerGuestMilli: 1000, courseLabel: null, name: ci.name, priceCents: ci.priceCents, allergens: ci.allergens, dietary: ci.dietary }]);
    setPickerOpen(false);
  }

  return (
    <>
      <PageHeader
        title="Pakete"
        subtitle="Buchbare Bündel & Preis-Anker (Preis pro Gast) – sie leiten die KI-Angebotserstellung."
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => navigate("/katalog")}>
              <Icon name="arrow_back" className="text-[18px]" /> Zum Katalog
            </button>
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Icon name="add" className="text-[18px]" /> Paket anlegen
            </button>
          </>
        }
      />

      {formError && <ErrorNote message={formError} />}

      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorNote message={error} />
      ) : packages.length === 0 ? (
        <EmptyState icon="inventory_2" title="Noch keine Pakete" hint="Lege ein Paket mit Preis pro Gast an – die KI nutzt es als Anker für realistische Angebote." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {packages.map((p) => (
            <div key={p.id} className="card flex flex-col gap-3 p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">{p.name}</p>
                  {p.description && <p className="mt-0.5 line-clamp-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{p.description}</p>}
                </div>
                <span className="flex shrink-0 items-center gap-1.5">
                  {p.hasArchivedItems && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 font-label-md text-[11px] font-semibold text-amber-700 dark:text-amber-300"
                      title="Enthält archivierte Positionen (z. B. nach »Karte ersetzen«) — Paket prüfen und ggf. Gänge austauschen."
                    >
                      <Icon name="warning" className="text-[13px]" /> prüfen
                    </span>
                  )}
                  {!p.active && <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-label-md dark:bg-surface-variant/40">inaktiv</span>}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display-lg text-headline-sm font-bold text-primary">{money(p.pricePerPersonCents)}</span>
                <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">/ Gast · {p.itemCount ?? 0} Positionen</span>
              </div>
              {/* LMIV: Allergen-Union über alle Gänge — IMMER sichtbar, solange Positionen existieren.
                  Unvollständig gepflegte Gänge werden ausdrücklich markiert (nie stilles „keine"). */}
              {(p.itemCount ?? 0) > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-label-md text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Allergene</span>
                  {p.allergensResolved === false ? (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 font-label-md text-[11px] font-semibold text-amber-700 dark:text-amber-300"
                      title="Mindestens ein Gang ohne gepflegte Allergenangabe – bitte im Katalog prüfen (LMIV)"
                    >
                      <Icon name="warning" className="text-[13px]" /> unvollständig
                    </span>
                  ) : null}
                  <AllergenBadges
                    allergens={p.allergensResolved === false && (p.allergenUnion ?? []).length === 0 ? null : (p.allergenUnion ?? [])}
                    dietary={p.dietaryAll}
                  />
                </div>
              )}
              <div className="mt-auto flex items-center gap-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
                <button type="button" className="btn-secondary flex-1 justify-center" onClick={() => setEditing(p)}>
                  <Icon name="edit" className="text-[16px]" /> Bearbeiten
                </button>
                <button type="button" className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30" title={p.active ? "Deaktivieren" : "Aktivieren"} onClick={() => void patchPkg(p, { active: !p.active })}>
                  <Icon name={p.active ? "toggle_on" : "toggle_off"} />
                </button>
                <button type="button" className="rounded-lg p-2 text-error hover:bg-error/10" title="Löschen" onClick={() => void removePkg(p)}>
                  <Icon name="delete" className="text-[18px]" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Anlegen */}
      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Paket anlegen">
        <form className="space-y-4" onSubmit={(e) => void saveNew(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold">Name *</span>
            <input name="name" required className="input" placeholder="z. B. Menü Klassik" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold">Beschreibung</span>
            <input name="description" className="input" placeholder="kurz, optional" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold">Preis pro Gast (€) *</span>
            <input name="price" required inputMode="decimal" className="input" placeholder="45,00" />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Speichert …" : "Anlegen"}</button>
          </div>
        </form>
      </Drawer>

      {/* Bearbeiten inkl. Positionen */}
      <Drawer open={editing != null} onClose={() => setEditing(null)} title={editing ? `Paket – ${editing.name}` : ""}>
        {editing && (
          <div className="space-y-6">
            {/* A-07: Fehler (ungueltiger Preis, 409 beim Positions-Speichern) auch IM Drawer
                anzeigen - auf Seitenebene lag die Meldung sonst unsichtbar hinter dem Overlay. */}
            {formError && <ErrorNote message={formError} />}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Preis pro Gast (€)</span>
                <input
                  className="input"
                  defaultValue={(editing.pricePerPersonCents / 100).toFixed(2)}
                  inputMode="decimal"
                  onBlur={(e) => {
                    const cents = parseGermanEurosToCents(e.target.value);
                    if (cents == null) return setFormError("Ungültiger Preis pro Gast – z. B. 45,00.");
                    void patchPkg(editing, { pricePerPersonCents: cents });
                  }}
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Name</span>
                <input className="input" defaultValue={editing.name} onBlur={(e) => e.target.value.trim() && void patchPkg(editing, { name: e.target.value.trim() })} />
              </label>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Enthaltene Positionen</h4>
                <button type="button" className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary hover:underline disabled:opacity-40" disabled={!itemsLoaded} onClick={() => setPickerOpen(true)}>
                  <Icon name="add_circle" className="text-[16px]" /> aus Katalog
                </button>
              </div>
              {/* M-47: Ladefehler sperrt das Editieren - sonst wuerde der naechste Save die
                  (nicht angezeigten) Bestandspositionen per Complete-List-PUT loeschen. */}
              {itemsLoadError ? (
                <div className="space-y-2 rounded-lg border border-error/40 bg-error/5 p-4">
                  <p className="font-body-sm text-body-sm text-error">Positionen konnten nicht geladen werden: {itemsLoadError}</p>
                  <button type="button" className="btn-secondary !py-1.5" onClick={() => loadItems(editing.id)}>Erneut laden</button>
                </div>
              ) : !itemsLoaded ? (
                <p className="rounded-lg border border-dashed border-outline-variant/50 p-4 text-center font-body-sm text-body-sm text-secondary dark:border-outline/40 dark:text-secondary-fixed-dim">
                  Positionen werden geladen …
                </p>
              ) : items.length === 0 ? (
                <p className="rounded-lg border border-dashed border-outline-variant/50 p-4 text-center font-body-sm text-body-sm text-secondary dark:border-outline/40 dark:text-secondary-fixed-dim">
                  Noch keine Positionen – füge welche aus dem Katalog hinzu.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {items.map((it) => (
                    <li key={it.id} className="flex items-center gap-2 rounded-lg border border-outline-variant/30 px-3 py-2 dark:border-outline/30">
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{it.name ?? "?"}</span>
                          {it.itemArchivedAt != null && (
                            <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 font-label-md text-[10px] font-semibold text-amber-700 dark:text-amber-300" title="Diese Position wurde archiviert (z. B. Karte ersetzt) — Gang austauschen oder entfernen.">
                              archiviert
                            </span>
                          )}
                        </span>
                        {/* LMIV: Allergene/Diät je Gang direkt an der Position sichtbar. */}
                        <AllergenBadges allergens={it.allergens} dietary={it.dietary} className="mt-0.5" />
                      </span>
                      <input
                        className="input h-8 w-20 text-center text-body-sm"
                        title="Menge pro Gast"
                        defaultValue={(it.qtyPerGuestMilli / 1000).toString()}
                        inputMode="decimal"
                        onBlur={(e) => {
                          const q = Math.round((parseFloat(e.target.value.replace(",", ".")) || 0) * 1000);
                          void saveItems(items.map((x) => (x.id === it.id ? { ...x, qtyPerGuestMilli: q } : x)));
                        }}
                      />
                      <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">/Gast</span>
                      <button type="button" className="rounded p-1 text-error hover:bg-error/10" title="Entfernen" onClick={() => void saveItems(items.filter((x) => x.id !== it.id))}>
                        <Icon name="close" className="text-[16px]" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={() => setEditing(null)}>Fertig</button>
            </div>
          </div>
        )}
      </Drawer>

      {/* Katalog-Picker für Paket-Positionen */}
      <Drawer open={pickerOpen} onClose={() => setPickerOpen(false)} title="Position aus Katalog">
        {catalogQ.loading ? (
          <LoadingRows />
        ) : (catalogQ.data ?? []).length === 0 ? (
          /* CX-Audit 2026-07-17: „Lege zuerst Positionen an" war Text ohne Weg — der CTA
             führt jetzt direkt in den Katalog statt den Nutzer im Drawer festsitzen zu lassen. */
          <EmptyState
            icon="menu_book"
            title="Katalog leer"
            hint="Lege zuerst Positionen im Katalog an."
            cta={{ label: "Zum Katalog", icon: "arrow_forward", onClick: () => navigate("/katalog") }}
          />
        ) : (
          <div className="space-y-1">
            {(catalogQ.data ?? []).map((ci) => (
              <button
                key={ci.id}
                type="button"
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface-container-high/40 dark:hover:bg-surface-variant/20"
                onClick={() => addCatalogItem(ci)}
              >
                <span className="min-w-0">
                  <span className="block truncate font-body-md text-body-md font-medium text-on-background dark:text-inverse-on-surface">{ci.name}</span>
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{money(ci.priceCents)}</span>
                    {/* LMIV: Allergene schon bei der Auswahl sichtbar. */}
                    <AllergenBadges allergens={ci.allergens} dietary={ci.dietary} />
                  </span>
                </span>
                <Icon name="add_circle" className="text-primary" />
              </button>
            ))}
          </div>
        )}
      </Drawer>
    </>
  );
}
